import { measureLatency } from '../../gctl-measure';
import {
  PrivateCommentsFields,
  PrivateComments,
} from '../../models/components/schemas/private-comments/PrivateComments';
import {
  Select,
  NetworkError,
  deepCamelCase,
  deepSnakeCase,
  searchQueryString,
  intoSnakeCase,
} from '../../fetchers/_helpers';

/**
 * This file was automatically generated by @gctl/openapi-fetchers
 * Do not modify this file, any changes will be destroyed when
 * the command is rerun.
 */

export type GetVideoPrivateCommentsFields = readonly PrivateCommentsFields[];

/** Options for GET /videos/{video_id}/private_comments */
export type GetVideoPrivateCommentsOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/json */
  select: Paths;
  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
  };

  /** Possible query parameters for /videos/{video_id}/private_comments */
  query?: {
    /**
     * The type of comments to return.
     *
     * Option descriptions:
     *  * `non_team` - Return comments from non-team members.
     *  * `team` - Return comments from team members.
     *
     */
    commentedUserType?: `non_team` | `team`;

    /**
     * The sort direction of the results.
     *
     * Option descriptions:
     *  * `asc` - Sort the results in ascending order.
     *  * `desc` - Sort the results in descending order.
     *
     */
    direction?: `asc` | `desc`;

    /**
     * Whether to include deleted comments.
     */
    includeDeletedComments?: boolean;

    /**
     * Whether to show comments where the user is mentioned or replied to.
     */
    mentionsAndReplies?: boolean;

    /**
     * The note ID to include in the paginated comments.
     */
    noteId?: number;

    /**
     * The page number of the results to show.
     */
    page?: number;

    /**
     * The password for the review link.
     */
    password?: string;

    /**
     * The number of items to show on each page of results, up to a maximum of 100.
     */
    perPage?: number;

    /**
     * The ID of the review link used as context.
     */
    reviewId?: string;

    /**
     * The way to sort the results.
     *
     * Option descriptions:
     *  * `date` - Sort the results by date.
     *  * `time_code` - Sort the results by time code.
     *
     */
    sort?: `date` | `time_code`;

    /**
     * The type of comments to return.
     *
     * Option descriptions:
     *  * `closed` - Return resolved comments.
     *  * `open` - Return open comments.
     *
     */
    status?: `closed` | `open`;

    /**
     * The URI of the version, which includes an upload attempt ID.
     */
    versionUri?: string;
  };
};

/** Result type for GET /videos/{video_id}/private_comments */
export type GetVideoPrivateCommentsResult<Paths extends readonly string[]> = {
  /** The total number of results for GET /videos/{video_id}/private_comments */
  total: number;
  /** The current page */
  page: number;
  /** The number of results per page specified in the request */
  perPage: number;
  /** Available API endpoints for traversing the paginated data */
  paging: {
    /** The next page, if available */
    next: string | null;
    /** The previous page, if available */
    previous: string | null;
    /** The first page */
    first: string;
    /** The last page */
    last: string;
  };
  /** An array containing the current results */
  data: Select<PrivateComments, Paths>[];
};

/**
 * Get all the private comments associated with a video
 * This method returns every private comment associated with the specified video.
 */
export async function getVideoPrivateComments<Paths extends GetVideoPrivateCommentsFields>({
  baseUrl,
  select,
  where: { videoId },
  query,
  ...options
}: GetVideoPrivateCommentsOptions<Paths>): Promise<GetVideoPrivateCommentsResult<Paths>> {
  return measureLatency('getVideoPrivateComments', 'GET', async () => {
    let res = await fetch(
      `${baseUrl}/videos/${videoId}/private_comments?${searchQueryString(query)}&fields=${select
        .map(intoSnakeCase)
        .join(',')}`,
      {
        ...options,
        method: 'GET',
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as GetVideoPrivateCommentsResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}

export type PostVideoPrivateCommentsFields = readonly PrivateCommentsFields[];

/** Options for POST /videos/{video_id}/private_comments */
export type PostVideoPrivateCommentsOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/json */
  select: Paths;
  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
  };

  /** Possible query parameters for /videos/{video_id}/private_comments */
  query?: {
    /**
     * The password for the review link.
     */
    password?: string;

    /**
     * The ID of the review link used as context.
     */
    reviewId?: string;
  };

  /** Data serialized to body for POST /videos/{video_id}/private_comments */
  variables: {
    /**
     * The page where the comment was made.
     *
     * Option descriptions:
     *  * `review` - The comment was made on the review page.
     *  * `svv` - The comment was made on the single video view page.
     *  * `viewer_home` - The comment was made on the viewer home page.
     *
     * @example svv
     */

    appOrigin?: `review` | `svv` | `viewer_home`;

    coordinates?: {
      /**
       * The X coordinate of the private comment's position. This value is expressed as a proportion of the video's width in pixels.
       * @example 0.5
       */

      x?: number;

      /**
       * The Y coordinate of the private comment's position. This value is expressed as a proportion of the video's width in pixels.
       * @example 0.75
       */

      y?: number;
    };

    /**
     * The ID of the guest user.
     * @example e58ed763-928c-4155-bee9-fdbaaadc15f3a
     */

    guestUserId?: string;

    /**
     * The guest name used when the user isn't logged in.
     * @example Ruth Bader Ginsburg
     */

    name?: string;

    /**
     * The ID of the review link used as context.
     * @example e58ed763-928c-4155-bee9-fdbaaadc15f3a
     */

    reviewLinkId?: string;

    /**
     * The rich comment in JSON stringified form.
     * @example {"type":"doc","content":[{"type":"paragraph","content":[{"type":"text","text":"Hi Tom"}]}]}
     */

    richtext?: string;

    /**
     * The comment text.
     * @example This scene can be removed
     */

    text: string;

    /**
     * The time in seconds for when the private comment appears in the video.
     * @example 300
     */

    timeCode?: number;

    /**
     * The URI of the version, which includes an upload attempt ID.
     * @example /videos/417226633/versions/322029195
     */

    versionUri?: string;
  };
};

/** Result type for POST /videos/{video_id}/private_comments */
export type PostVideoPrivateCommentsResult<Paths extends readonly string[]> = Select<
  PrivateComments,
  Paths
>;

/**
 * Add a private comment to a video
 * This method adds a private comment to the single video page. The comment appears only when the video is accessed through its page on Vimeo.
 */
export async function postVideoPrivateComments<Paths extends PostVideoPrivateCommentsFields>({
  baseUrl,
  select,
  variables,
  where: { videoId },
  query,
  ...options
}: PostVideoPrivateCommentsOptions<Paths>): Promise<PostVideoPrivateCommentsResult<Paths>> {
  return measureLatency('postVideoPrivateComments', 'POST', async () => {
    let res = await fetch(
      `${baseUrl}/videos/${videoId}/private_comments?${searchQueryString(query)}&fields=${select
        .map(intoSnakeCase)
        .join(',')}`,
      {
        ...options,
        method: 'POST',
        body: JSON.stringify(deepSnakeCase(variables)),
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as PostVideoPrivateCommentsResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}
